// Valor efectivo de un evento para todo cálculo de cobro (saldos, flag
// pagado, "por cobrar"). La cotización (desglose, con IVA si aplica) manda
// cuando existe; precioAcordado es el fallback para eventos sin desglose.
// Aunque al editar el desglose se sincroniza precioAcordado, los eventos
// creados antes de esa sincronización pueden traerlo desactualizado — por
// eso los cálculos derivan siempre de aquí y no del campo crudo.

// Total de un desglose (con IVA si aplica), redondeado a centavos.
export function totalDesglose(desglose, ivaEnabled) {
  const subtotal = (Array.isArray(desglose) ? desglose : []).reduce(
    (sum, item) => sum + (Number(item.units) * Number(item.unitPrice) || 0),
    0
  );
  const total = ivaEnabled ? subtotal * 1.16 : subtotal;
  return Math.round(total * 100) / 100;
}

export function getValorEvento(event) {
  const desglose = Array.isArray(event?.desglose) ? event.desglose : [];
  if (desglose.length > 0) {
    const total = totalDesglose(desglose, event?.ivaEnabled);
    if (total > 0) return total;
  }
  return Number(event?.precioAcordado || 0);
}

// true cuando el valor efectivo viene de la cotización y difiere del precio
// acordado capturado — para señalarlo en la UI.
export function valorVieneDeCotizacion(event) {
  const desglose = Array.isArray(event?.desglose) ? event.desglose : [];
  if (desglose.length === 0) return false;
  const valor = getValorEvento(event);
  return (
    valor > 0 && Math.abs(valor - Number(event?.precioAcordado || 0)) >= 0.01
  );
}

// Tolerancia de un centavo: el total con IVA (×1.16) produce fracciones que
// harían fallar una comparación >= exacta contra pagos capturados en pesos.
export function eventoPagado(event, totalPagado) {
  const valor = getValorEvento(event);
  const pagado =
    totalPagado != null ? Number(totalPagado) : Number(event?.totalPagado || 0);
  return valor > 0 && pagado >= valor - 0.01;
}

// Línea de descuento por porcentaje. Se calcula sobre el subtotal, pero como
// el IVA es proporcional, descontar X% del subtotal descuenta exactamente X%
// del total final — "10% del total" se cumple con o sin IVA.
export function lineaDescuentoPorcentaje({ desglose, pct, nombre = "Descuento" }) {
  const subtotal = (Array.isArray(desglose) ? desglose : []).reduce(
    (sum, item) => sum + (Number(item.units) * Number(item.unitPrice) || 0),
    0
  );
  const monto = Math.round(subtotal * (Number(pct) / 100) * 100) / 100;
  if (!(monto > 0)) return null;
  return {
    concept: `${nombre.trim() || "Descuento"} (${Number(pct)}%)`,
    units: 1,
    unitPrice: -monto,
  };
}

// Línea de ajuste que deja el total final (con IVA si aplica) en pesos
// cerrados. El unitPrice conserva toda la precisión (p. ej. -0.27586…) para
// que el total recalculado quede exacto; la UI lo muestra a centavos.
export function lineaRedondeo({ desglose, ivaEnabled, direccion }) {
  const total = totalDesglose(desglose, ivaEnabled);
  if (!(total > 0)) return null;
  const objetivo = direccion === "arriba" ? Math.ceil(total) : Math.floor(total);
  const delta = Math.round((objetivo - total) * 100) / 100;
  if (delta === 0) return null;
  return {
    concept: "Redondeo",
    units: 1,
    unitPrice: ivaEnabled ? delta / 1.16 : delta,
  };
}

// Detecta estados incongruentes entre cotización, precio y pagos, para que
// la UI los muestre en lugar de ocultarlos en un "Pagado" engañoso.
export function inconsistenciasEvento(event, totalPagado) {
  const valor = getValorEvento(event);
  const pagado =
    totalPagado != null ? Number(totalPagado) : Number(event?.totalPagado || 0);
  const desglose = Array.isArray(event?.desglose) ? event.desglose : [];
  const out = [];
  if (desglose.length > 0 && totalDesglose(desglose, event?.ivaEnabled) <= 0) {
    out.push("desglose_en_cero");
  }
  if (valor > 0 && pagado > valor + 0.01) {
    out.push("sobrepago");
  }
  if (valor <= 0 && pagado > 0) {
    out.push("pagos_sin_valor");
  }
  return out;
}
